import { EnquiryAudienceEnum, UserClassificationType } from 'generated/graphql';
import type { User } from '~/server/types';
import { isUserLoggedIn } from '~/modules/user/hook/use-user';

/**
 * Result of the enquiry visibility check.
 */
export interface EnquiryVisibilityResult {
    /** Whether to show enquiry CTA buttons (header, mobile). */
    showEnquiryCTA: boolean;
    /** Whether to show the inline enquiry form. */
    showEnquiryForm: boolean;
    /** Whether to show the specifier login prompt. */
    showSpecifierLoginPrompt: boolean;
}

/**
 * Parameters for determining enquiry visibility.
 */
interface EnquiryVisibilityParams {
    /** The professional's enquiry audience setting. */
    enquiryAudience: EnquiryAudienceEnum;
    /** The current user (logged in or guest). */
    user: User;
}

/**
 * Determines whether enquiry CTAs, forms, and login prompts should be visible
 * based on the professional's enquiry audience setting and the user's classification.
 *
 * @param params - The parameters for determining visibility.
 * @returns The visibility result for enquiry elements.
 *
 * Logic:
 * - `Anybody`: Show all (current behaviour)
 * - `NoEnquiries`: Hide all CTAs and forms
 * - `SpecifiersOnly`:
 *   - Logged in + UserClassification === 'Professional': Show all
 *   - Logged in + UserClassification === 'Homeowner': Hide all
 *   - Logged out: Hide CTAs, show login prompt
 */
export function getEnquiryVisibility(
    params: EnquiryVisibilityParams,
): EnquiryVisibilityResult {
    const { enquiryAudience, user } = params;

    // Default result - show everything (matches current behaviour for 'Anybody')
    const showAll: EnquiryVisibilityResult = {
        showEnquiryCTA: true,
        showEnquiryForm: true,
        showSpecifierLoginPrompt: false,
    };

    const hideAll: EnquiryVisibilityResult = {
        showEnquiryCTA: false,
        showEnquiryForm: false,
        showSpecifierLoginPrompt: false,
    };

    // Handle 'Anybody' - show all (current default behaviour)
    if (enquiryAudience === EnquiryAudienceEnum.Anybody) {
        return showAll;
    }

    // Handle 'NoEnquiries' - hide all CTAs and forms
    if (enquiryAudience === EnquiryAudienceEnum.NoEnquiries) {
        return hideAll;
    }

    // Handle 'SpecifiersOnly'
    if (enquiryAudience === EnquiryAudienceEnum.SpecifiersOnly) {
        // Check if user is logged in
        if (isUserLoggedIn(user)) {
            // Logged in user - check their classification
            const userClassification = user.UserClassification;

            if (userClassification === UserClassificationType.Professional) {
                // Specifier (professional) - show all
                return showAll;
            }

            // Homeowner or undefined classification - hide all
            return hideAll;
        }

        // Logged out user - show login prompt instead of form
        return {
            showEnquiryCTA: false,
            showEnquiryForm: false,
            showSpecifierLoginPrompt: true,
        };
    }

    // Fallback to show all (should not reach here)
    return showAll;
}
